import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/joy";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/Key";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "guest", label: "Guest" },
  { value: "agent", label: "Agent" },
];

const EditUserModal = ({ open, onClose, user, onUpdateUser, onSetPassword }) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    role: "user",
    is_active: true,
  });
  const [newPassword, setNewPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (open && user) {
      setFormData({
        username: user.username || "",
        email: user.email || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        role: user.role || "user",
        is_active: user.is_active ?? true,
      });
      setErrors({});
      setNewPassword("");
      setPasswordError("");
    }
  }, [open, user]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSwitchChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const result = await onUpdateUser(user.id, formData);
    setIsSubmitting(false);

    if (result.success) {
      onClose();
    } else if (result.errors) {
      const backendErrors = {};
      Object.entries(result.errors).forEach(([key, value]) => {
        backendErrors[key] = Array.isArray(value) ? value[0] : value;
      });
      setErrors(backendErrors);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword) {
      setPasswordError("Password is required");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }

    setIsChangingPassword(true);
    const result = await onSetPassword(user.id, newPassword);
    setIsChangingPassword(false);

    if (result.success) {
      setNewPassword("");
      setPasswordError("");
    } else {
      setPasswordError(result.error || "Failed to change password");
    }
  };

  if (!user) return null;

  return (
    <Modal
      aria-labelledby="edit-user-modal-title"
      open={open}
      onClose={onClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: 500,
          width: "100%",
          borderRadius: "md",
          p: 3,
          boxShadow: "lg",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.surface",
        }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} onClick={onClose} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <EditIcon color="primary" />
          <Typography
            component="h2"
            id="edit-user-modal-title"
            level="h4"
            textColor="inherit"
            sx={{ fontWeight: "lg" }}
          >
            Edit User
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormControl error={!!errors.username} required>
            <FormLabel>Username</FormLabel>
            <Input
              placeholder="Enter username"
              value={formData.username}
              onChange={handleChange("username")}
            />
            {errors.username && (
              <Typography level="body-xs" color="danger">
                {errors.username}
              </Typography>
            )}
          </FormControl>

          <FormControl error={!!errors.email}>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              placeholder="Enter email (optional)"
              value={formData.email}
              onChange={handleChange("email")}
            />
            {errors.email && (
              <Typography level="body-xs" color="danger">
                {errors.email}
              </Typography>
            )}
          </FormControl>

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl error={!!errors.first_name} required sx={{ flex: 1 }}>
              <FormLabel>First Name</FormLabel>
              <Input
                placeholder="First name"
                value={formData.first_name}
                onChange={handleChange("first_name")}
              />
              {errors.first_name && (
                <Typography level="body-xs" color="danger">
                  {errors.first_name}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.last_name} required sx={{ flex: 1 }}>
              <FormLabel>Last Name</FormLabel>
              <Input
                placeholder="Last name"
                value={formData.last_name}
                onChange={handleChange("last_name")}
              />
              {errors.last_name && (
                <Typography level="body-xs" color="danger">
                  {errors.last_name}
                </Typography>
              )}
            </FormControl>
          </Box>

          <FormControl error={!!errors.role}>
            <FormLabel>Role</FormLabel>
            <Select
              value={formData.role}
              onChange={(_, value) => handleChange("role")(value)}
            >
              {ROLES.map((role) => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl
            orientation="horizontal"
            sx={{ justifyContent: "space-between" }}
          >
            <Box>
              <FormLabel>Active</FormLabel>
              <Typography level="body-xs" color="neutral">
                User can log in and access the system
              </Typography>
            </Box>
            <Switch
              checked={formData.is_active}
              onChange={handleSwitchChange("is_active")}
              color={formData.is_active ? "success" : "neutral"}
            />
          </FormControl>

          <Divider sx={{ my: 1 }} />

          <Accordion>
            <AccordionSummary>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <KeyIcon fontSize="small" />
                <Typography level="title-sm">Change Password</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <FormControl error={!!passwordError}>
                  <FormLabel>New Password</FormLabel>
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordError("");
                    }}
                  />
                  {passwordError && (
                    <Typography level="body-xs" color="danger">
                      {passwordError}
                    </Typography>
                  )}
                </FormControl>
                <Button
                  size="sm"
                  variant="outlined"
                  color="primary"
                  onClick={handleChangePassword}
                  loading={isChangingPassword}
                  disabled={!newPassword}
                >
                  Update Password
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <Button onClick={onClose} variant="plain">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="solid"
            color="primary"
            loading={isSubmitting}
          >
            Save Changes
          </Button>
        </Box>
      </Sheet>
    </Modal>
  );
};

export default EditUserModal;
